import * as React from 'react';
import { observer } from 'mobx-react';
import { action, observable } from 'mobx';
import { Label, Icon } from 'office-ui-fabric-react';
import ReactTable, { CellInfo, Column } from 'react-table';

import { ApiUrls, Permissions } from '@app/AppConstants';
import { PersonFunctionViewModel, PlanningFunctionOverruleModel, PlanningFunctionOverruleValidationResult } from '@app/Models';

import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import ApiService from '@app/Services/ApiService';
import DateTimeService from '@app/Services/DateTimeService';
import InlineDateValidationService from '@app/Services/InlineDateValidationService';
import { securityService } from '@app/Services/SecurityService';

import { NewFunctionOverruleDialog } from '@app/Pages/PersonInfo/Components/Dialogs/NewFunctionOverruleDialog';
import { InlineOverruleTypeDropdownCell } from '@app/Components/CellFormatters/InlineOverruleTypeDropdownCell';
import { InlineDateCell } from '@app/Components/CellFormatters/InlineDateCell';
import { InlineTextboxCellWithTooltip } from '@app/Components/CellFormatters/InlineTextboxCellWithTooltip';
import { NotificationHandler } from '@app/Components/ToastNotification';
import { modalService, IModalDialogContent, ModalButtonType, ModalWindow, ModalDialogOptions } from '@app/Components/Modal';
import { AzureAuth } from '@app/Components/AzureAuth';

export type PersonFunctionOverruleUpdateCallback = () => void;

export type PlanningFunctionOverrulesDialogProps = {
    personFunction: PersonFunctionViewModel;
    onPersonFunctionOverruleUpdateHandler: PersonFunctionOverruleUpdateCallback;
};

@observer
export class PlanningFunctionOverrulesDialog extends React.Component<PlanningFunctionOverrulesDialogProps, {}> implements IModalDialogContent<void> {
    
    private _store: PlanningFunctionOverrulesDialogStore;
    
    constructor(props: PlanningFunctionOverrulesDialogProps) {
        super(props);
        this._store = new PlanningFunctionOverrulesDialogStore(props);
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        return {
            title: 'Function overrules',
            width: '700px',
            buttons: [{
                type: ModalButtonType.Cancel,
                onClick: () => {
                    window.close();
                }
            }],
            loader: this._store.loader
        };
    }

    render() {
        let headerDateRange = this.props.personFunction.dateFrom ? '( ' + DateTimeService.toUiDate(this.props.personFunction.dateFrom) + ' / ' : '( - / ';
        headerDateRange += this.props.personFunction.dateTo ? DateTimeService.toUiDate(this.props.personFunction.dateTo) + ' )' : '- )';

        if (!this.props.personFunction.dateFrom && !this.props.personFunction.dateTo) {
            headerDateRange = '';
        }
        const {hasPermission, permissionCss} = securityService;
        const cls = permissionCss(Permissions.ViewObjectPersonFunctionOverrule, Permissions.EditObjectPersonFunctionOverrule);
        
        return (
            <>
                <div className="table-label">
                    <Label><span className="license-headline">For '{this.props.personFunction.functionName}' {headerDateRange} </span> </Label>
                    {hasPermission(Permissions.EditObjectPersonFunctionOverrule, !AzureAuth.isReadOnly) &&
                    <button 
                        className={'table-label-button ' + cls}
                        onClick={this._addFunctionOverrule}
                    >
                        <Icon iconName="Add" className="ms-IconTable" />
                    </button>}
                </div>
                <ReactTable<PlanningFunctionOverruleModel>
                    data={this._store.personFunctionOverrules}
                    columns={this._functionOverruleColumns}
                    defaultPageSize={10}
                    showPageSizeOptions={false}
                    resizable={true}
                    showPagination={false}
                    className={'ReactTable-fixed-height-modal'}
                />
            </>
        );
    }

    @action.bound
    private async _addFunctionOverrule() {
        const {personFunction, onPersonFunctionOverruleUpdateHandler } = this.props;
        const { button } = await modalService.show(NewFunctionOverruleDialog, {
            functionDateFrom: personFunction.dateFrom,
            functionDateTo: personFunction.dateTo,
            functionId: personFunction.id
        });

        if (button === ModalButtonType.Save){
            await this._store.loadPlanningFunctionOverrule(this.props.personFunction.id);
        }

        onPersonFunctionOverruleUpdateHandler();
    }

    private _renderInactivationDateFrom = (cellInfo: CellInfo) => {
        return <InlineDateCell<PlanningFunctionOverruleModel>
            onValidate={InlineDateValidationService.validateInactivationDate} 
            value={cellInfo.value} 
            onSelect={this._updateFunctionOverrule}
            row={cellInfo.row._original} 
            fieldKey="dateFrom"
            minDate={this.props.personFunction.dateFrom} 
            maxDate={this.props.personFunction.dateTo}
            isReadOnly={!securityService.hasPermission(Permissions.EditPersonFunctionOverruleDateFrom, !AzureAuth.isReadOnly)} />;
    };
    
    private _renderInactivationDateTo = (cellInfo: CellInfo) => {
        return <InlineDateCell<PlanningFunctionOverruleModel>
            onValidate={InlineDateValidationService.validateInactivationDate} 
            value={cellInfo.value} 
            onSelect={this._updateFunctionOverrule}
            row={cellInfo.row._original} 
            fieldKey={cellInfo.column.id as keyof PlanningFunctionOverruleModel}
            minDate={this.props.personFunction.dateFrom} 
            maxDate={this.props.personFunction.dateTo}
            isReadOnly={!securityService.hasPermission(Permissions.EditPersonFunctionOverruleDateTo, !AzureAuth.isReadOnly)} />;
    };

    private _renderInactivationComment = (cellInfo: CellInfo) => {
        return <InlineTextboxCellWithTooltip<PlanningFunctionOverruleModel>
            cellInfo={cellInfo} 
            value={cellInfo.value} 
            onChange={this._updateFunctionOverrule} 
            row={cellInfo.row._original} 
            fieldKey="comment" 
            disabled={!securityService.hasPermission(Permissions.EditPersonFunctionOverruleComment, !AzureAuth.isReadOnly)} 
        />;
    };

    private _renderFunctionOverruleType = (cellInfo: CellInfo) => {
        return <InlineOverruleTypeDropdownCell 
                    value={cellInfo.value} 
                    onChange={this._updateFunctionOverrule} 
                    row={cellInfo.row._original}
                    disabled={!securityService.hasPermission(Permissions.EditPersonFunctionOverruleType, !AzureAuth.isReadOnly)} />;
    };

    private _renderDeleteFunctionOverruleColumn = (cellInfo: CellInfo) => {
        return <i className="ms-Icon ms-Icon-action ms-Icon--Delete" aria-hidden="true" onClick={async () => this._onDeleteFunctionverrule(cellInfo)} />;
    };

    private async _onDeleteFunctionverrule(cellInfo: CellInfo) {
        await modalService.showConfirmation('Are you sure you want to delete this function overrule?', 'Warning', {
            onConfirm: async () => {
                await this._store.deleteFunctionOverrule(cellInfo.value, this.props.personFunction.id);
            }
        });
    }

    private _updateFunctionOverrule = (params: PlanningFunctionOverruleModel) => {
        this._store.updateFunctionOverrule(params);
    };

    private _functionOverruleColumns: Column<PlanningFunctionOverruleModel>[] = [
        {
            Header: 'Overrule type',
            accessor: 'functionOverruleType',
            Cell: this._renderFunctionOverruleType,
            show: securityService.hasPermission(Permissions.ViewPersonFunctionOverruleType),
            className: securityService.permissionCss(Permissions.ViewPersonFunctionOverruleType, Permissions.EditPersonFunctionOverruleType)
        },
        {
            Header: 'From',
            accessor: 'dateFrom',
            Cell: this._renderInactivationDateFrom,
            maxWidth: 150,
            show: securityService.hasPermission(Permissions.ViewPersonFunctionOverruleDateFrom),
            className: securityService.permissionCss(Permissions.ViewPersonFunctionOverruleDateFrom, Permissions.EditPersonFunctionOverruleDateFrom)
        },
        {
            Header: 'To',
            accessor: 'dateTo',
            Cell: this._renderInactivationDateTo,
            maxWidth: 150,
            show: securityService.hasPermission(Permissions.ViewPersonFunctionOverruleDateTo),
            className: securityService.permissionCss(Permissions.ViewPersonFunctionOverruleDateTo, Permissions.EditPersonFunctionOverruleDateTo)
        },
        {
            Header: 'Comment',
            accessor: 'comment',
            Cell: this._renderInactivationComment,
            show: securityService.hasPermission(Permissions.ViewPersonFunctionOverruleComment),
            className: securityService.permissionCss(Permissions.ViewPersonFunctionOverruleComment, Permissions.EditPersonFunctionOverruleComment)
        },
        {
            Header: '',
            accessor: 'id',
            sortable: false,
            Cell: this._renderDeleteFunctionOverruleColumn,
            maxWidth: 50,
            className: securityService.permissionCss(Permissions.ViewObjectPersonFunctionOverrule, Permissions.EditObjectPersonFunctionOverrule),
            show: securityService.hasPermission(Permissions.EditObjectPersonFunctionOverrule, !AzureAuth.isReadOnly)
        }
    ];
}

class PlanningFunctionOverrulesDialogStore{
    @observable personFunctionOverrules: PlanningFunctionOverruleModel[];
    private onChangeHandler: PersonFunctionOverruleUpdateCallback;
    public loader: PromiseCompletion = new PromiseCompletion();
    
    constructor(params: PlanningFunctionOverrulesDialogProps){
        this.onChangeHandler = params.onPersonFunctionOverruleUpdateHandler;
        this.loadPlanningFunctionOverrule(params.personFunction.id);
    }

    @action
    async loadPlanningFunctionOverrule(functionId: string) {
        const params = { personFunctionId: functionId };
        const { data: overrules } = await ApiService.getTypedData<PlanningFunctionOverruleModel[]>(ApiUrls.PersonFunctionOverruleUrl, params, { completion: this.loader });
        this.personFunctionOverrules = overrules;
    }

    @action.bound
    async updateFunctionOverrule(params: PlanningFunctionOverruleModel) {
        const { data: result } = await ApiService.putTypedData<PlanningFunctionOverruleValidationResult>(ApiUrls.PersonFunctionOverruleUrl, params);
        if (result.hasErrors && result.errors) {
            await modalService.showInformation(result.errors.join('\r\n '));
        } else {
            NotificationHandler.showSuccess('Person Function Overrule was updated', 'Success message');
            this.onChangeHandler();
        }

        const updatedFunction = result.value as PlanningFunctionOverruleModel;
        if (updatedFunction) {
            this.personFunctionOverrules = this.personFunctionOverrules.map((item: PlanningFunctionOverruleModel) => item.id === updatedFunction.id ? updatedFunction : item);
        }
    }

    @action.bound
    async deleteFunctionOverrule(overruleId: string, functionId: string) {
        await ApiService.deleteData(ApiUrls.PersonFunctionOverruleUrl + '?overruleId=' + overruleId);
        NotificationHandler.showSuccess('Person Function Overrule was deleted', 'Success message');
        this.loadPlanningFunctionOverrule(functionId);
        this.onChangeHandler();
    }
}